import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCurrentUser } from '../../services/serviceability-service';
import { apiFetch } from '../../utils/apiClient';
const BASE_URL = 'https://dbc-a971b0e4-d539.cloud.databricks.com/api/2.0/preview/scim/v2';

// Async thunks
export const fetchUsers = createAsyncThunk(
    'user/fetchUsers',
    async (_, { rejectWithValue }) => {
        try {
            return await apiFetch(`${BASE_URL}/Users`, { method: 'GET' });
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Async thunk to fetch current user from /users/me endpoint
export const getCurrentUser = createAsyncThunk(
    'user/getCurrentUser',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchCurrentUser();
            return response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch user data');
        }
    }
);

// Initial state
const initialState = {
    users: [],
    currentUser: null, // Current user from /users/me endpoint
    isLoading: false,
    error: null,
    isInitialized: false, // Track if current user has been fetched
};

// User slice
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        // Action to manually set current user data
        setCurrentUser: (state, action) => {
            state.currentUser = action.payload;
            state.isInitialized = true;
        },
        // Action to clear current user data (e.g., on logout)
        clearCurrentUser: (state) => {
            state.currentUser = null;
            state.isInitialized = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Users
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.users = action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Get Current User
            .addCase(getCurrentUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentUser = action.payload;
                state.isInitialized = true;
                state.error = null;
            })
            .addCase(getCurrentUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
                state.isInitialized = true;
            });
    },
});

export const { clearError, setCurrentUser, clearCurrentUser } = userSlice.actions;

// Selectors
export const selectCurrentUser = (state) => state.user.currentUser;
export const selectUserLoading = (state) => state.user.isLoading;
export const selectUserError = (state) => state.user.error;
export const selectUserInitialized = (state) => state.user.isInitialized;

// Convenience selectors for specific user properties
export const selectUserID = (state) => state.user.currentUser?.ID;
export const selectUserEmail = (state) => state.user.currentUser?.Email;
export const selectDataProviderID = (state) => state.user.currentUser?.DataProviderID;
export const selectDataProviderName = (state) => state.user.currentUser?.DataProviderName;

export default userSlice.reducer;
